import { Badge } from "@/components/ui/badge";
import { Calendar, GitBranch } from "lucide-react";
import { Database } from "@/integrations/supabase/types";
import ChangelogEntry from "./ChangelogEntry";
import { useTranslation } from "react-i18next";

type Version = Database['public']['Tables']['versions']['Row'];
type ChangelogEntryType = Database['public']['Tables']['changelog_entries']['Row'];

interface VersionWithEntries extends Version {
  changelog_entries: ChangelogEntryType[];
}

interface VersionCardProps {
  version: VersionWithEntries;
}

const VersionCard = ({ version }: VersionCardProps) => {
  const { t, i18n } = useTranslation('changelog');

  const formatDate = (dateString: string) => {
    const locale = i18n.language === 'de' ? 'de-DE' : 'en-US';
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="bg-card border rounded-lg p-6 mb-6 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-semibold text-foreground">
            Version {version.version_number}
          </h2>
          {version.is_pre_release && (
            <Badge variant="secondary">{t('version.preRelease')}</Badge>
          )}
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Calendar className="w-4 h-4" />
          {formatDate(version.release_date)}
        </div>
      </div>
      
      {version.release_notes && (
        <p className="text-muted-foreground mb-4">{version.release_notes}</p>
      )}

      {version.git_tag && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
          <GitBranch className="w-4 h-4" />
          <code className="bg-muted px-2 py-1 rounded text-xs">{version.git_tag}</code>
        </div>
      )}

      <div className="space-y-3">
        {version.changelog_entries
          .sort((a, b) => a.sort_order - b.sort_order)
          .map((entry) => (
            <ChangelogEntry key={entry.id} entry={entry} />
          ))}
      </div>
    </div>
  );
};

export default VersionCard;